// client/src/pages/business/Login.js
// ============================================
// BUSINESS LOGIN PAGE
// Updated to handle pending/rejected account statuses
// ============================================

import React, { useState, useEffect } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PasswordField from '../../components/common/PasswordField';

const BusinessLogin = () => {
  const { t } = useTranslation(['auth', 'common']);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, isAuthenticated, user, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [redirect, setRedirect] = useState(false);

  // Redirect if already logged in as business
  useEffect(() => {
    if (isAuthenticated && user?.role === 'business') {
      setRedirect(true);
    }
  }, [isAuthenticated, user]);

  // Handle input change
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ✅ UPDATED - Handles pending/rejected account status redirects
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { success, error: loginError, accountStatus, rejectionReason, companyName } = await login(formData);

      if (success) {
        setRedirect(true);
      } else {
        // Check if this is an account status error (pending/rejected/suspended)
        if (accountStatus && accountStatus !== 'approved') {
          // Redirect to AccountPending page with status info
          navigate('/account-pending', {
            state: {
              accountStatus,
              rejectionReason,
              companyName,
              userType: 'business'
            }
          });
        } else {
          setError(loginError);
        }
      }
    } catch (err) {
      // Handle axios error responses
      const response = err.response?.data;
      
      if (response?.accountStatus && response.accountStatus !== 'approved') {
        // Backend returned account status error
        navigate('/account-pending', {
          state: {
            accountStatus: response.accountStatus,
            rejectionReason: response.rejectionReason,
            companyName: response.companyName,
            userType: 'business'
          }
        });
      } else {
        setError(err.message || t('auth:login.invalidCredentials'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while auth state is initializing
  if (authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Redirect if login succeeded
  if (redirect) {
    const redirectTo = location.state?.from?.pathname || '/dashboard';
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <Navbar />

      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <h1 className="text-3xl font-bold text-blue-600 mb-2">
          {t('auth:login.title')}
        </h1>
        <p className="text-gray-600">
          {t('auth:login.subtitle')}
        </p>
        <p className="mt-2 text-xs text-blue-600 font-medium">
          {t('auth:login.businessOnly')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('auth:login.email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder={t('auth:login.emailPlaceholder')}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('auth:login.password')}
              </label>
              <PasswordField
                id="password"
                name="password"
                required
                value={formData.password}
                onChange={handleChange}
                placeholder={t('auth:login.passwordPlaceholder')}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link 
                  to="/forgot-password" 
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  {t('auth:login.forgotPassword')}
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? <LoadingSpinner size="sm" /> : t('auth:login.button')}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  {t('auth:login.noAccount')}
                </span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/register"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                {t('auth:login.registerLink')}
              </Link>
            </div>

            <div className="mt-4 text-center">
              <Link
                to="/wholesaler/login"
                className="text-sm text-gray-600 hover:text-gray-500"
              >
                {t('auth:login.wholesalerLogin')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BusinessLogin;